import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { useCoachProfileInColumn } from '@lib/pga-coach/coach-profile-layout'
import useStyles from './coach-profile-testimonials.styles'

const HEADING_ID = 'coach-profile-testimonials-heading'
const HEADING = 'Testimonials'

const WRAPPER_QUOTES = /^[\s\u201C\u201D\u2018\u2019"']+|[\s\u201C\u201D\u2018\u2019"']+$/g

function displayTestimonial(text) {
  return String(text).replace(WRAPPER_QUOTES, '')
}

/**
 * Coach-profile-specific testimonial band. Renders the same
 * `testimonialsJsonData` array consumed by the shared `TestimonialV2`
 * component, but as a normal flagged-profile section: the same
 * heading as Photos / Services / Request Coaching
 * (`variant="h5"` `component="h2"`), then body-sized quote
 * cards. No slider, no photo background, no overlay — every
 * testimonial is rendered inline so visitors can scan all of them at
 * once before reaching the facilities section below.
 *
 * Intentionally a wrapper rather than a modification to `TestimonialV2`:
 * other layouts (home, workingolf, university, etc.) still rely on the
 * core slider/markup behavior. Flag-off legacy still uses `TestimonialV2`.
 */
function CoachProfileTestimonials({ testimonialsData = [] }) {
  const inColumn = useCoachProfileInColumn()
  const styles = useStyles({ inColumn })

  const entries = (Array.isArray(testimonialsData) ? testimonialsData : [])
    .filter((entry) => entry && entry.testimonial)

  if (entries.length === 0) return null

  return (
    <Box
      component="section"
      className={styles.root}
      aria-labelledby={HEADING_ID}
      data-testid="coach-profile-testimonials"
    >
      <Box className={styles.inner}>
        <Typography
          id={HEADING_ID}
          variant="h5"
          component="h2"
          className={styles.heading}
        >
          {HEADING}
        </Typography>
        <Box component="ul" className={styles.list}>
          {entries.map((entry, index) => {
            const author = entry.author && entry.author.trim()
            const key = `${author || 'testimonial'}-${index}`
            return (
              <Box component="li" key={key} className={styles.item}>
                <Box component="blockquote" className={styles.blockquote}>
                  <Typography component="p" variant="body1" className={styles.quote}>
                    {displayTestimonial(entry.testimonial)}
                  </Typography>
                  {author && (
                    <Typography component="cite" variant="body2" className={styles.attribution}>
                      {`\u2014 ${author}`}
                    </Typography>
                  )}
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

CoachProfileTestimonials.propTypes = {
  testimonialsData: PropTypes.arrayOf(PropTypes.shape({
    testimonial: PropTypes.string,
    author: PropTypes.string,
    rank: PropTypes.number,
  })),
}

export default CoachProfileTestimonials
